import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import QRCode from "qrcode";
import { api } from "../lib/api";
import { fecha } from "../lib/formato";
import { Aviso, Cabecera, Campo, Cargando, Modal, Panel, Vacio } from "../componentes/piezas";
import { useIdioma } from "../lib/idioma";
import type { Clave } from "../idiomas/es";

/**
 * Las cartas del club: lo que se enseña en la pantalla del local o detrás de
 * un QR en la mesa.
 *
 * Antes había una sola. Un club con barra y dispensario quiere dos listas
 * distintas —en la barra no pinta nada el catálogo de flores— y quien tiene
 * una sala aparte quiere una tercera. Cada una lleva su propio enlace, así que
 * renovar el de la barra no tumba el del dispensario.
 */

interface Carta {
  id: string;
  nombre: string;
  area: "DISPENSARIO" | "BARRA";
  porDefecto: boolean;
  activa: boolean;
  token: string;
  mensaje: string | null;
  categorias: string[];
  ruta: string;
  createdAt: string;
}

interface Respuesta {
  slug: string;
  categorias: { id: string; nombre: string; color: string }[];
  cartas: Carta[];
}

const AREAS: { clave: Carta["area"]; titulo: Clave; icono: string }[] = [
  { clave: "DISPENSARIO", titulo: "ca.dispensario", icono: "❁" },
  { clave: "BARRA", titulo: "ca.barra", icono: "☕" },
];

export default function Cartas() {
  const { t } = useIdioma();
  const clienteConsultas = useQueryClient();
  const [creando, setCreando] = useState<Carta["area"] | null>(null);
  const [editando, setEditando] = useState<Carta | null>(null);
  const [viendoQR, setViendoQR] = useState<Carta | null>(null);
  const [error, setError] = useState("");

  const datos = useQuery({ queryKey: ["cartas"], queryFn: () => api<Respuesta>("/carta") });
  const refrescar = () => void clienteConsultas.invalidateQueries({ queryKey: ["cartas"] });

  async function hacer(accion: () => Promise<unknown>) {
    setError("");
    try {
      await accion();
      refrescar();
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : t("ca.fallo"));
    }
  }

  if (datos.isLoading) return <Cargando />;
  const cartas = datos.data?.cartas ?? [];

  return (
    <>
      <Cabecera titulo={t("ca.titulo")} descripcion={t("ca.descripcion")} />

      <Aviso tipo="error">{error}</Aviso>

      <div className="rejilla dos" style={{ alignItems: "start" }}>
        {AREAS.map((area) => {
          const suyas = cartas.filter((c) => c.area === area.clave);
          return (
            <Panel key={area.clave} titulo={`${area.icono} ${t(area.titulo)}`}>
              {suyas.length === 0 ? (
                <Vacio>
                  {area.clave === "BARRA" ? t("ca.sinCartaBarra") : t("ca.sinCartaDispensario")}
                </Vacio>
              ) : (
                <ul className="lista-cartas">
                  {suyas.map((c) => (
                    <li key={c.id} className={c.activa ? undefined : "apagada"}>
                      <div style={{ minWidth: 0 }}>
                        <div className="fila" style={{ gap: 8 }}>
                          <strong>{c.nombre}</strong>
                          {c.porDefecto && (
                            <span className="etiqueta" title={t("ca.principalAyuda")}>
                              {t("ca.principal")}
                            </span>
                          )}
                          {!c.activa && <span className="etiqueta roja">{t("ca.apagada")}</span>}
                        </div>
                        <div className="suave pequeno">
                          {t("ca.creadaEl", { fecha: fecha(c.createdAt) })}
                          {c.categorias.length > 0 && t("ca.nCategorias", { n: c.categorias.length })}
                        </div>
                      </div>

                      <div className="fila" style={{ gap: 2 }}>
                        <button
                          className="boton fantasma"
                          onClick={() => setViendoQR(c)}
                          title={t("ca.verQr")}
                        >
                          {t("ca.qr")}
                        </button>
                        <button className="boton fantasma" onClick={() => setEditando(c)}>
                          {t("ca.editar")}
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              <button className="boton secundario" style={{ marginTop: 12 }} onClick={() => setCreando(area.clave)}>
                {t("ca.nuevaDe", { zona: t(area.titulo).toLowerCase() })}
              </button>
            </Panel>
          );
        })}
      </div>

      <p className="suave pequeno" style={{ marginTop: 14 }}>
        {t("ca.notaPrincipal1")} <strong>{t("ca.laPrincipal")}</strong> {t("ca.notaPrincipal2")}
      </p>

      {creando && (
        <DialogoNueva
          area={creando}
          alCerrar={() => setCreando(null)}
          alHecho={() => {
            setCreando(null);
            refrescar();
          }}
          alFallar={setError}
        />
      )}

      {editando && (
        <DialogoCarta
          carta={editando}
          categorias={datos.data?.categorias ?? []}
          alCerrar={() => setEditando(null)}
          alHecho={() => {
            setEditando(null);
            refrescar();
          }}
          alFallar={setError}
        />
      )}

      {viendoQR && <DialogoQR carta={viendoQR} alCerrar={() => setViendoQR(null)} alRenovar={hacer} />}
    </>
  );
}

function DialogoNueva({
  area,
  alCerrar,
  alHecho,
  alFallar,
}: {
  area: Carta["area"];
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (m: string) => void;
}) {
  const { t } = useIdioma();
  const [nombre, setNombre] = useState(
    area === "BARRA" ? t("ca.n.nombreBarra") : t("ca.n.nombreDispensario"),
  );
  const [enviando, setEnviando] = useState(false);

  return (
    <Modal
      titulo={t("ca.n.titulo")}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || nombre.trim().length < 2}
            onClick={async () => {
              setEnviando(true);
              try {
                await api("/carta", { metodo: "POST", cuerpo: { nombre, area } });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("ca.n.fallo"));
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("ca.n.crear")}
          </button>
        </>
      }
    >
      <Campo etiqueta={t("ca.n.nombre")} ayuda={t("ca.n.nombreAyuda")}>
        <input value={nombre} onChange={(e) => setNombre(e.target.value)} autoFocus maxLength={60} />
      </Campo>
      <p className="suave pequeno" style={{ marginBottom: 0 }}>
        {area === "BARRA" ? t("ca.n.avisoBarra") : t("ca.n.avisoDispensario")}
      </p>
    </Modal>
  );
}

function DialogoCarta({
  carta,
  categorias,
  alCerrar,
  alHecho,
  alFallar,
}: {
  carta: Carta;
  categorias: { id: string; nombre: string; color: string }[];
  alCerrar: () => void;
  alHecho: () => void;
  alFallar: (m: string) => void;
}) {
  const { t } = useIdioma();
  const [nombre, setNombre] = useState(carta.nombre);
  const [mensaje, setMensaje] = useState(carta.mensaje ?? "");
  const [activa, setActiva] = useState(carta.activa);
  const [elegidas, setElegidas] = useState<string[]>(carta.categorias);
  const [enviando, setEnviando] = useState(false);
  const [borrando, setBorrando] = useState(false);

  const alternar = (id: string) =>
    setElegidas((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));

  return (
    <Modal
      titulo={`Carta · ${carta.nombre}`}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={alCerrar}>
            Cancelar
          </button>
          <button
            className="boton"
            disabled={enviando || nombre.trim().length < 2}
            onClick={async () => {
              setEnviando(true);
              try {
                await api(`/carta/${carta.id}`, {
                  metodo: "PATCH",
                  cuerpo: {
                    nombre,
                    mensaje: mensaje.trim() || null,
                    activa,
                    ...(carta.area === "DISPENSARIO" ? { categorias: elegidas } : {}),
                  },
                });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("ca.e.falloGuardar"));
                alCerrar();
              } finally {
                setEnviando(false);
              }
            }}
          >
            {t("g.guardar")}
          </button>
        </>
      }
    >
      <Campo etiqueta={t("ca.e.nombre")}>
        <input value={nombre} onChange={(e) => setNombre(e.target.value)} maxLength={60} />
      </Campo>

      <Campo etiqueta={t("ca.e.mensaje")} ayuda={t("ca.e.mensajeAyuda")}>
        <input value={mensaje} onChange={(e) => setMensaje(e.target.value)} maxLength={300} />
      </Campo>

      {carta.area === "DISPENSARIO" && (
        <Campo
          etiqueta={t("ca.e.categorias")}
          ayuda={elegidas.length === 0 ? t("ca.e.categoriasAyuda") : undefined}
        >
          <div className="fila" style={{ flexWrap: "wrap", gap: 6 }}>
            {categorias.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`boton ${elegidas.includes(c.id) ? "" : "secundario"}`}
                onClick={() => alternar(c.id)}
              >
                {c.nombre}
              </button>
            ))}
          </div>
        </Campo>
      )}

      <label className="casilla">
        <input type="checkbox" checked={activa} onChange={(e) => setActiva(e.target.checked)} />
        <span>
          {t("ca.e.encendida")}
          <br />
          <span className="ayuda">{t("ca.e.encendidaAyuda")}</span>
        </span>
      </label>

      {!carta.porDefecto && (
        <div className="fila separada" style={{ marginTop: 8 }}>
          <span className="suave pequeno">{t("ca.e.ponerlaEnPantalla")}</span>
          <button
            className="boton secundario"
            onClick={async () => {
              try {
                await api(`/carta/${carta.id}`, { metodo: "PATCH", cuerpo: { porDefecto: true } });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("ca.fallo"));
                alCerrar();
              }
            }}
          >
            {t("ca.e.hacerlaPrincipal")}
          </button>
        </div>
      )}

      {!carta.porDefecto && (
        <div className="fila separada" style={{ marginTop: 14 }}>
          <span className="suave pequeno">{t("ca.e.yaNoLaUsais")}</span>
          <button
            className="boton peligro"
            disabled={borrando}
            onClick={async () => {
              if (!window.confirm(t("ca.e.confirmarBorrar", { nombre: carta.nombre }))) return;
              setBorrando(true);
              try {
                await api(`/carta/${carta.id}`, { metodo: "DELETE" });
                alHecho();
              } catch (fallo) {
                alFallar(fallo instanceof Error ? fallo.message : t("ca.e.falloBorrar"));
                alCerrar();
              } finally {
                setBorrando(false);
              }
            }}
          >
            {t("ca.e.borrar")}
          </button>
        </div>
      )}
    </Modal>
  );
}

/** El QR y el enlace de una carta, para pegarlo en una mesa o abrirlo en la tablet. */
function DialogoQR({
  carta,
  alCerrar,
  alRenovar,
}: {
  carta: Carta;
  alCerrar: () => void;
  alRenovar: (accion: () => Promise<unknown>) => Promise<void>;
}) {
  const { t } = useIdioma();
  const [svg, setSvg] = useState("");
  const enlace = `${window.location.origin}${carta.ruta}`;

  useEffect(() => {
    void QRCode.toString(enlace, { type: "svg", margin: 1, width: 240 }).then(setSvg);
  }, [enlace]);

  return (
    <Modal
      titulo={t("ca.q.titulo", { nombre: carta.nombre })}
      alCerrar={alCerrar}
      acciones={
        <>
          <button className="boton secundario" onClick={() => window.print()}>
            {t("d.imprimir")}
          </button>
          <button className="boton" onClick={alCerrar}>
            {t("g.cerrar")}
          </button>
        </>
      }
    >
      {!carta.activa && (
        <Aviso tipo="atencion">{t("ca.q.apagada")}</Aviso>
      )}

      <div className="documento" style={{ textAlign: "center" }}>
        <div className="qr-carta" dangerouslySetInnerHTML={{ __html: svg }} />
        <strong>{carta.nombre}</strong>
      </div>

      <Campo etiqueta={t("ca.q.enlace")}>
        <input readOnly value={enlace} onFocus={(e) => e.currentTarget.select()} />
      </Campo>

      <div className="fila separada sin-imprimir">
        <span className="suave pequeno">{t("ca.q.siSeHaFiltrado")}</span>
        <button
          className="boton secundario"
          onClick={async () => {
            if (!window.confirm(t("ca.q.confirmarRenovar"))) return;
            await alRenovar(() => api(`/carta/${carta.id}`, { metodo: "PATCH", cuerpo: { renovarToken: true } }));
            alCerrar();
          }}
        >
          {t("ca.q.cambiarEnlace")}
        </button>
      </div>

      <p className="suave pequeno sin-imprimir" style={{ marginBottom: 0 }}>
        {t("ca.q.soloEsta")}
      </p>
    </Modal>
  );
}
